import {
  createSlice,
  createSelector,
  createAsyncThunk,
  createEntityAdapter,
} from "@reduxjs/toolkit";
import { uncoveryApi } from "services";
import { queryStringFromArray } from "helpers";
import _flattenDeep from "lodash/flattenDeep";
import orderBy from "lodash/orderBy";

const assetAdapter = createEntityAdapter();
const initialState = assetAdapter.getInitialState({
  errorMessage: "",
  fetchAllAssetStatus: "idle",
  fetchOneAssetStatus: "idle",
  fetchAllTypesStatus: "idle",
  selectedAsset: {},
  collectionId: "",
  fetchAllStatus: "idle",
  updateFavoriteStatus: "idle",
  updateFavoriteErrorMessage: "",
  fetchAllFavoritesStatus: "idle",
  fetchFiltersStatus: "idle",
  fetchFiltersErrorMessage: "",
  archiveAssetStatus: "idle",
  archiveAssetErrorMessage: "",
  exportAssetStatus: "idle",
  exportAssetErrorMessage: "",
  hasNextPage: false,
  count: 0,
  page: 1,
  pageSize: 50,
  sortBy: "status",
  orderBy: "asc",
  searchTerm: "",
  filters: [],
  filtersV2: {
    searchTerm: "",
    values: [],
  },
  filtersStack: [],
  values: [],
  dataExport: null,
  scrollPosition: 0,
  isCheckedAll: false,
  favoriteFilters: [],
  statusFilters: [],
  typeFilters: [],
});

export const fetchAllAssets = createAsyncThunk(
  "asset/fetchAllAssets",
  async ({ entityId, mapId, collectionId }, { rejectWithValue, getState }) => {
    const {
      page,
      pageSize,
      sortBy,
      orderBy,
      searchTerm,
      statusFilters,
      typeFilters,
    } = getState().asset;
    const queryStatusFilters = queryStringFromArray(statusFilters, "status");
    const queryTypeFilters = queryStringFromArray(typeFilters, "type");
    const queryCollectionId = collectionId
      ? `&collectionId=${collectionId}`
      : "";
    const query = `/entities/${entityId}/assets${
      mapId ? `?cartographyId=${mapId}` : `?`
    }${queryCollectionId}&page=${page}&pageSize=${pageSize}&sortBy=${sortBy}&orderBy=${orderBy}&searchTerm=${encodeURIComponent(
      searchTerm
    )}${queryStatusFilters ? `&${queryStatusFilters}` : ""}${
      queryTypeFilters ? `&${queryTypeFilters}` : ""
    }`;
    try {
      const response = await uncoveryApi.get(query);
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const fetchOneAsset = createAsyncThunk(
  "asset/fetchOneAsset",
  async ({ entityId, assetId }, { rejectWithValue }) => {
    const query = `/entities/${entityId}/assets/${assetId}`;
    try {
      const response = await uncoveryApi.get(query);
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const fetchFilters = createAsyncThunk(
  "asset/fetchFilters",
  async ({ entityId, mapId, collectionId }, { rejectWithValue, getState }) => {
    const { searchTerm } = getState().asset.filtersV2;
    const { statusFilters } = getState().asset;
    const queryStatusFilters = queryStringFromArray(statusFilters, "status");
    const query = `/entities/${entityId}/assets/filters/list?cartographyId=${
      mapId || ""
    }&collectionId=${collectionId || ""}&searchTerm=${searchTerm}${
      queryStatusFilters ? `&${queryStatusFilters}` : ""
    }`;
    try {
      const response = await uncoveryApi.get(query);
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const fetchFilterValues = createAsyncThunk(
  "asset/fetchFilterValues",
  async ({ entityId, mapId, type }, { rejectWithValue, getState }) => {
    const { collectionId } = getState().asset;
    const { statusFilters } = getState().asset;
    const queryStatusFilters = queryStringFromArray(statusFilters, "status");
    const index = getState().asset.filters.findIndex((f) => f.type === type);
    const { searchTerm } = getState().asset.filtersV2;
    const { page, pageSize } = getState().asset.filters[index];
    const query = `/entities/${entityId}/assets/filters?cartographyId=${
      mapId || ""
    }&collectionId=${
      collectionId || ""
    }&type=${type}&page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&groupBy=value${
      queryStatusFilters ? `&${queryStatusFilters}` : ""
    }`;
    try {
      const response = await uncoveryApi.get(query);
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const archiveAsset = createAsyncThunk(
  "asset/archiveAsset",
  async ({ entityId, assetIds }, { rejectWithValue }) => {
    const qsAssetIds = queryStringFromArray(assetIds, "assetId");
    const query = `/entities/${entityId}/assets?${qsAssetIds}`;
    const body = {
      archived: true,
    };
    try {
      const response = await uncoveryApi.put(query, body);
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const exportAssets = createAsyncThunk(
  "asset/exportAssets",
  async ({ entityId, mapId, collectionId }, { rejectWithValue, getState }) => {
    const { searchTerm, sortBy, orderBy } = getState().asset;
    const { statusFilters } = getState().asset;
    const queryStatusFilters = queryStringFromArray(statusFilters, "status");
    const query = `/entities/${entityId}/assets/download?cartographyId=${
      mapId || ""
    }&collectionId=${
      collectionId || ""
    }&sortBy=${sortBy}&orderBy=${orderBy}&searchTerm=${searchTerm}${
      queryStatusFilters ? `&${queryStatusFilters}` : ""
    }`;
    try {
      const response = await uncoveryApi.get(query);
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const updateFavoriteFilter = createAsyncThunk(
  "asset/addFavoriteFilter",
  async ({ entityId, type, isFavorite }, { rejectWithValue, getState }) => {
    const query = `/entities/${entityId}/assets/filters?${type}=${isFavorite}`;
    try {
      const response = await uncoveryApi.put(query);
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const assetSlice = createSlice({
  name: "asset",
  initialState,
  reducers: {
    refreshFetchAllStatus: (state) => {
      state.fetchAllStatus = "idle";
      return state;
    },
    resetSearchTerm: (state) => {
      state.searchTerm = "";
      return state;
    },
    resetExportTable: (state) => {
      state.exportAssetStatus = "idle";
      state.exportAssetErrorMessage = "";
      state.dataExport = null;
      return state;
    },
    updateSearchTerm: (state, { payload }) => {
      const { searchTerm } = payload;
      state.page = 1;
      state.searchTerm = searchTerm;
      return state;
    },
    updateSearchTermFilters: (state, { payload }) => {
      const { searchTerm } = payload;
      state.filtersV2.searchTerm = searchTerm;
      return state;
    },
    setScrollPosition: (state, { payload }) => {
      const { scrollPosition } = payload;
      state.scrollPosition = scrollPosition;
      return state;
    },
    sort: (state, { payload }) => {
      const { sortBy } = payload;
      state.page = 1;
      if (sortBy !== state.sortBy) {
        state.orderBy = "asc";
        state.sortBy = sortBy;
      } else {
        state.orderBy = state.orderBy === "desc" ? "asc" : "desc";
      }
      return state;
    },
    updatePage: (state, { payload }) => {
      const { page } = payload;
      state.page = page;
      return state;
    },
    nextFilterPage: (state, { payload }) => {
      const { type } = payload;
      const indexFilter = state.filters.findIndex((f) => f.type === type);
      state.filters[indexFilter].page = state.filters[indexFilter].page + 1;
      return state;
    },
    refreshFetchFilterStatus: (state) => {
      state.fetchFiltersStatus = "idle";
      return state;
    },
    refreshUpdateFavoriteStatus: (state) => {
      state.updateFavoriteStatus = "idle";
      return state;
    },
    filterToggled: (state, { payload }) => {
      const { type } = payload;
      const filters = state.filters.map((f) => {
        if (f.type !== type) {
          return f;
        }
        return {
          ...f,
          toggled: !f.toggled,
          isActive: true,
        };
      });
      state.filters = filters;
      return state;
    },
    filterValueToggled: (state, { payload }) => {
      const { type, value } = payload;
      const indexFilter = state.filters.findIndex((f) => f.type === type);
      const indexValue = state.filters[indexFilter].values.findIndex(
        (f) => f.value === value
      );
      // Toggle selected / unselected filter value
      const indexValueSelected = state.filters[
        indexFilter
      ].valuesSelected.findIndex(
        (x) => x.value === state.filters[indexFilter].values[indexValue].value
      );
      // If value not selected add to the selected array, else remove it
      if (indexValueSelected === -1) {
        state.filters[indexFilter].valuesSelected.push(
          state.filters[indexFilter].values[indexValue]
        );
      } else {
        state.filters[indexFilter].valuesSelected.splice(indexValueSelected, 1);
      }
      // Add to searchTerm
      state.searchTerm = _flattenDeep(
        state.filters.map((f) => {
          return f.valuesSelected.map((v) => {
            return `${f.type}:"${v.value}"`;
          });
        })
      ).join(" or ");

      state.filters[indexFilter].values[indexValue].isSelected =
        !state.filters[indexFilter].values[indexValue].isSelected;
      return state;
    },
    checkValue: (state, { payload }) => {
      const { assetId } = payload;
      const index = state.values.findIndex((e) => e.id === assetId);
      state.values[index].isChecked = !state.values[index].isChecked;
      return state;
    },
    checkAllValues: (state, { payload }) => {
      const { isChecked } = payload;
      state.isCheckedAll = isChecked;
      state.values.forEach((e) => (e.isChecked = isChecked));
      return state;
    },
    setCollectionId: (state, { payload }) => {
      const { collectionId } = payload;
      state.collectionId = collectionId;
    },
    setStatusFilters: (state, { payload }) => {
      const { statusFilters } = payload;
      state.page = 1;
      state.statusFilters = statusFilters.map((s) => s.value);
    },
    setTypeFilters: (state, { payload }) => {
      const { typeFilters } = payload;
      state.page = 1;
      state.typeFilters = typeFilters.map((t) => t.value);
    },
    // updateCollectionsAssets: (state, { payload }) => {
    //   const { assetId, collectionId, action } = payload;
    //   const mapCollectionId = collectionId.map((c) => {
    //     return { id: c.id };
    //   });
    //   assetId.forEach((id) => {
    //     const index = state.values.findIndex((a) => a.id === id);
    //     if (action === "add") {
    //       state.values[index].collections = [
    //         ...state.values[index].collections,
    //         ...mapCollectionId,
    //       ];
    //     } else if (action === "remove") {
    //       state.values[index].collections.filter(
    //         (c) => !collectionId.includes(c.id)
    //       );
    //     }
    //   });
    //   return state;
    // },
    resetAssetState: () => initialState,
    resetSelectedAsset: (state) => {
      state.selectedAsset = null;
      state.fetchOneAssetStatus = "idle";
      return state;
    },
    resetAssets: (state) => {
      state.fetchAllStatus = "idle";
      state.updateFavoriteStatus = "idle";
      state.updateFavoriteErrorMessage = "";
      state.fetchAllFavoritesStatus = "idle";
      state.fetchFiltersStatus = "idle";
      state.fetchFiltersErrorMessage = "";
      state.archiveAssetStatus = "idle";
      state.archiveAssetErrorMessage = "";
      state.exportAssetStatus = "idle";
      state.exportAssetErrorMessage = "";
      state.hasNextPage = false;
      state.count = 0;
      state.page = 1;
      state.pageSize = 20;
      state.sortBy = "status";
      state.orderBy = "asc";
      state.searchTerm = "";
      state.filters = [];
      state.filtersV2 = {
        searchTerm: "",
        values: [],
      };
      state.filtersStack = [];
      state.errorMessage = "";
      state.values = [];
      state.dataExport = null; // for export
      state.scrollPosition = 0;
      state.isCheckedAll = false;
      state.favoriteFilters = [];
      return state;
    },
  },
  extraReducers: {
    [fetchAllAssets.pending]: (state, { payload, meta }) => {
      // const { } = meta.arg;
      state.fetchAllStatus = "loading";
    },
    [fetchAllAssets.fulfilled]: (state, { payload }) => {
      payload.data.forEach((e) => {
        e.isChecked = false;
      });
      if (state.page === 1) {
        state.values = payload.data;
      } else {
        state.values = [...state.values, ...payload.data];
      }
      state.fetchAllStatus = "success";
      state.hasNextPage = payload.pageInfo.hasNextPage;
      state.count = payload.pageInfo.count;
    },
    [fetchAllAssets.rejected]: (state, { payload }) => {
      state.errorMessage = payload.message;
      state.fetchAllStatus = "error";
    },
    [fetchFilters.pending]: (state, { payload }) => {
      state.fetchFiltersStatus = "loading";
    },
    [fetchFilters.fulfilled]: (state, { payload }) => {
      state.filters = payload.data.map((t, i) => {
        if (state.filtersStack.includes(t.value)) {
          const indexFilter = state.filters.findIndex(
            (f) => f.type === t.value
          );
          return state.filters[indexFilter];
        }
        return {
          count: t.count,
          type: t.value,
          isHidden: false,
          toggled: false,
          isActive: false,
          status: "idle",
          page: 1,
          pageSize: 50,
          searchTerm: "",
          hasNextPage: false,
          errorMessage: "",
          values: [],
          valuesSelected: [],
          isFavorite: t.isFavorite,
        };
      });
      state.fetchFiltersStatus = "success";
    },
    [fetchFilters.rejected]: (state, { payload }) => {
      state.fetchFiltersStatus = "error";
      state.errorMessage = payload.message;
      state.fetchFiltersErrorMessage = payload.message;
    },
    [fetchFilterValues.pending]: (state, { meta }) => {
      const { type } = meta.arg;
      const index = state.filters.findIndex((f) => f.type === type);
      state.filters[index].status = "loading";
    },
    [fetchFilterValues.fulfilled]: (state, { payload, meta }) => {
      const { type } = meta.arg;
      const index = state.filters.findIndex((f) => f.type === type);
      const values = payload.data.map((v) => {
        if (
          state.filters[index].valuesSelected.some((s) => s.value === v.value)
        ) {
          v.isSelected = true;
        } else {
          v.isSelected = false;
        }
        return v;
      });

      if (state.filters[index].page === 1) {
        state.filters[index].values = values;
      } else {
        state.filters[index].values = [
          ...state.filters[index].values,
          ...values,
        ];
      }
      state.filters[index].values.sort((a, b) =>
        a.isSelected > b.isSelected ? -1 : b.isSelected > a.isSelected ? 1 : 0
      );
      state.filters[index].status = "success";
      state.filters[index].hasNextPage = payload.pageInfo.hasNextPage;
    },
    [fetchFilterValues.rejected]: (state, { payload, meta }) => {
      const { type } = meta.arg;
      const index = state.filters.findIndex((f) => f.type === type);
      state.filters[index].status = "error";
      state.filters[index].errorMessage = payload.message;
    },
    [updateFavoriteFilter.pending]: (state, { payload }) => {
      state.updateFavoriteStatus = "loading";
    },
    [updateFavoriteFilter.fulfilled]: (state, { payload, meta }) => {
      const { type, isFavorite } = meta.arg;
      const filter = state.filters.find((element) => element.type === type);
      filter.isFavorite = isFavorite;
      state.filters = orderBy(
        state.filters,
        ["isFavorite", "type"],
        ["desc", "asc"]
      );
      state.updateFavoriteStatus = "success";
    },
    [updateFavoriteFilter.rejected]: (state, { payload }) => {
      state.updateFavoriteErrorMessage = payload.message;
      state.updateFavoriteStatus = "error";
    },
    [fetchOneAsset.pending]: (state) => {
      state.fetchOneAssetStatus = "loading";
    },
    [fetchOneAsset.fulfilled]: (state, { payload }) => {
      state.selectedAsset = payload.data;
      state.fetchOneAssetStatus = "success";
    },
    [fetchOneAsset.rejected]: (state, { payload }) => {
      state.errorMessage = payload.message;
      state.fetchOneAssetStatus = "error";
    },
    [archiveAsset.pending]: (state) => {
      state.archiveAssetStatus = "loading";
    },
    [archiveAsset.fulfilled]: (state) => {
      state.isCheckedAll = false;
      state.values.forEach((e) => (e.isChecked = false));
      state.archiveAssetStatus = "success";
    },
    [archiveAsset.rejected]: (state, { payload }) => {
      state.archiveAssetErrorMessage = payload.message;
      state.archiveAssetStatus = "error";
    },
    [exportAssets.pending]: (state) => {
      state.exportAssetStatus = "loading";
    },
    [exportAssets.fulfilled]: (state, { payload }) => {
      state.dataExport = payload;
      state.exportAssetStatus = "success";
    },
    [exportAssets.rejected]: (state, { payload }) => {
      state.exportAssetErrorMessage = payload.message;
      state.exportAssetStatus = "error";
    },
  },
});

export const {
  refreshFetchAllStatus,
  updateSearchTerm,
  updateSearchTermFilters,
  sort,
  updatePage,
  nextFilterPage,
  refreshUpdateFavoriteStatus,
  resetAssetState,
  filterToggled,
  filterValueToggled,
  resetSearchTerm,
  refreshFetchFilterStatus,
  resetSelectedAsset,
  setScrollPosition,
  checkValue,
  checkAllValues,
  fetchAllFavoritesStatus,
  setCollectionId,
  setStatusFilters,
  setTypeFilters,
  // updateCollectionsAssets,
  resetExportTable,
  resetAssets,
} = assetSlice.actions;

export default assetSlice.reducer;

// export const { state } = assetAdapter.getSelectors((state) => state.asset);
const assetState = (state) => state.asset;
// const getId = (_, id) => id;

export const selectAssetIds = createSelector(assetState, (asset) => {
  return asset.values.map((v) => v.id);
});

export const selectedFilters = createSelector(assetState, (asset) => {
  return _flattenDeep(
    asset.filters.map((f) => {
      return f.valuesSelected.map((v) => {
        return {
          type: f.type,
          value: v.value,
        };
      });
    })
  );
});

export const hasAssets = createSelector(assetState, (asset) => {
  return asset.values.length ? true : false;
});

export const selectAssets = (state) => state.asset.values;

export const selectAssetById = (state, assetId) => {
  return state.asset.values.find((v) => v.id === assetId);
};
